<template>

  <div>

    <b-card>
      <h2 class="mb-2">
        Search Filter
      </h2>
      <b-row>
        <b-col
          cols="12"
          md="4"
          class="w-100"
        >
          <v-select
            v-model="filterDataInstance"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filterInstanceOptions"
            :clearable="false"
            label="name"
            :reduce="option => option.id"
            :placeholder="'Select Instance'"
          />
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <div class="d-flex align-items-center">
            <b-button
              class="ml-1"
              variant="primary"
              @click="clearFilters"
            >
              <span class="mr-25 align-middle">Reset filters</span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table Container Card -->
    <b-card
      no-body
    >

      <div class="table-header m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mr-1 mb-0">Show</label>
            <v-select
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
              :value="perPage"
              @input="updatePerPage"
            />
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <label class="mr-1 mb-0">Search</label>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
                class="w-25"
              />

              <b-button
                v-b-modal.create-role-modal
                class="ml-1"
                variant="primary"
              >
                <span class="mr-25 align-middle">Create Role</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refRolesList"
        :items="fetchRolesList"
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :sort-by.sync="sortBy"
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
      >

        <!-- Loader -->
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <!-- Column: name -->
        <template #cell(display_name)="data">
          <b-link
            :to="{ name: 'admin-role-edit', params: { id: data.item.id } }"
            @click="selectRoleToEdit(data.item)"
          >{{ data.item.display_name }}</b-link>
        </template>

        <!-- Column: instances -->
        <template #cell(schools)="data">
          <span
            v-for="(school, index) in data.item.schools"
            :key="school.id"
          >{{ school.name }}<span v-if="index < data.item.schools.length - 1">, </span></span>
        </template>

        <!-- Column: created at -->
        <template #cell(created_at)="data">
          {{ data.item.created_at | formatDate }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">

          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="mx-auto p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              :to="{ name: 'admin-role-edit', params: { id: data.item.id } }"
            >
              <span>Edit</span>
            </b-dropdown-item>

            <b-dropdown-item
              :disabled="!data.item.is_deletable"
              @click="deleteItem(data.item)"
            >
              <span>Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>

      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>

    </b-card>

    <create-role-modal />
  </div>

</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BTable, BLink,
  BPagination, BDropdown, BDropdownItem, BButton, BSpinner,
} from 'bootstrap-vue'

import vSelect from 'vue-select'

import store from '@/store'

import storeModule from '@/views/admin/roles/rolesStoreModule'
import useRolesList from '@/views/admin/roles/roles-list/useRolesList'
import moment from 'moment'
import CreateRoleModal from '@/views/admin/roles/roles-list/modals/CreateRoleModal.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BLink,
    BPagination,
    BDropdown,
    BDropdownItem,
    BButton,
    BSpinner,

    vSelect,

    CreateRoleModal,
  },
  filters: {
    formatDate(value) {
      return value ? moment(value).format('YYYY-MM-DD') : ''
    },
  },
  setup(props, { root }) {
    const INVOICE_APP_STORE_MODULE_NAME = 'app-roles'

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) store.registerModule(INVOICE_APP_STORE_MODULE_NAME, storeModule)

    const {
      tableColumns,
      perPage,
      currentPage,
      dataMeta,
      perPageOptions,
      searchQuery,
      filterDataInstance,
      sortBy,
      totalItems,
      isSortDirDesc,
      refRolesList,
      fetchRolesList,
      fetchInstancesList,
      deleteRole,
      clearFilters,

      refetchData,
    } = useRolesList(root)

    return {
      tableColumns,
      perPage,
      currentPage,
      dataMeta,
      perPageOptions,
      searchQuery,
      filterDataInstance,
      sortBy,
      totalItems,
      isSortDirDesc,
      refRolesList,
      fetchRolesList,
      fetchInstancesList,
      deleteRole,
      clearFilters,

      refetchData,
    }
  },
  data() {
    return {
      filterInstanceOptions: [],
    }
  },
  created() {
    this.setFilterParams()
    this.fetchInstancesList().then(data => {
      this.filterInstanceOptions = data || []
    })
    // Update filters
    window.onpopstate = () => {
      this.setFilterParams()
    }
  },
  watch: {
    $route() {
      if (!this.$route.query.currentPage) {
        this.setFilterParams()
      }
      this.refetchData()
    },
  },
  methods: {
    async deleteItem(role) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the role "${role.display_name}"? This action cannot be undone.`,
        {
          title: 'Delete Role',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        },
      )
      if (confirmed) {
        await this.deleteRole(role.id)
      }
    },
    updatePerPage(val) {
      localStorage.setItem('rolesPerPage', val)
      this.perPage = val
    },
    setFilterParams() {
      const query = { ...this.$route.query }
      const defaultPerPage = Number(localStorage.getItem('rolesPerPage'))
      this.currentPage = Number(query.currentPage) || 1
      this.perPage = Number(query.perPage) || defaultPerPage || 10
      this.searchQuery = query.searchQuery || ''
    },
    selectRoleToEdit(roleData) {
      this.$store.commit('app-roles/SET_SELECTED_ROLE', roleData)
    },
  },
}
</script>

<style lang="scss" scoped>
  .table-header {

    label {
      margin-bottom: 0;
    }
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  .table-actions {
    width: 100px;
  }
</style>
